import { API_URL, headers } from '@modules/aawz-intelligence-v2/constants/API.js'

// `conexao_caiu` é o discriminante: quem reconecta (`apiAdvisory`) pergunta
// pela flag, nunca pela frase — a copy pode mudar sem quebrar a reconexão.
const CONNECTION_ERROR_RESPONSE = {
  status: 500,
  message: 'Erro de conexão',
  value: null,
  conexao_caiu: true,
}

/** Este resultado é queda de rede/parse do NOSSO lado, e não envelope do servidor? */
export function ehQuedaDeConexao(resposta) {
  return Boolean(resposta && resposta.conexao_caiu === true)
}

/** Uma cópia nova do envelope de queda de conexão — a fonte única do shape. */
export function respostaDeQuedaDeConexao() {
  return { ...CONNECTION_ERROR_RESPONSE }
}

/**
 * O envio que o PRÓPRIO cliente abortou ([T024]).
 *
 * 499 é o `client closed request` — o status que descreve o que houve sem
 * fingir que o servidor falhou. É a diferença que decide a copy na tela:
 * "não foi possível enviar; a conversa não foi salva" só vale quando o
 * servidor NÃO recebeu. No aborto ele recebeu tudo e seguiu — a tela é que
 * desistiu de escutar.
 *
 * Medido no E2E do [T023]: a tool de front automática navegou, o
 * `beforeDestroy` da view abortou o POST da continuação, e o gestor leu, sobre
 * a ficha recém-aberta, que a conversa não fora salva — enquanto o banco tinha
 * as duas mensagens do assistente.
 */
export const STATUS_ENVIO_CANCELADO = 499

const CANCEL_RESPONSE = {
  status: STATUS_ENVIO_CANCELADO,
  message: 'Envio cancelado.',
  value: null,
  cancelado: true,
}

/** O nome que `AbortController.abort()` dá ao erro, nos navegadores todos. */
const ERRO_DE_ABORTO = 'AbortError'

/**
 * Este resultado é um cancelamento nosso, e não uma falha?
 *
 * Discriminante ÚNICO — a flag, não o status. Quem consome (`apiChat`,
 * `view-chat`) pergunta por aqui em vez de comparar 499 espalhado por três
 * arquivos.
 */
export function foiCancelado(resposta) {
  return Boolean(resposta && resposta.cancelado === true)
}

/** Uma cópia nova do envelope de cancelamento — a fonte única do shape. */
export function respostaDeCancelamento() {
  return { ...CANCEL_RESPONSE }
}

/** O erro que veio do `fetch` é o nosso próprio `abort()`? */
export function ehAborto(erro) {
  return Boolean(erro && erro.name === ERRO_DE_ABORTO)
}

/**
 * O desfecho de um `fetch`/parse que estourou — e o único ponto onde se decide
 * se aquilo foi FALHA ou desistência nossa.
 *
 * Aborto é decisão nossa (o usuário saiu da tela, a tool de front navegou):
 * nem log de erro, nem shape de falha. Tratá-lo como queda de rede é o que
 * punha um "a conversa não foi salva" falso sobre um turno inteiro ([T024]).
 */
function desfechoDaFalha(error, contexto) {
  if (ehAborto(error)) return respostaDeCancelamento()
  console.error(contexto, error)
  return respostaDeQuedaDeConexao()
}

const STREAM_CONTENT_TYPE = 'text/event-stream'
const JSON_CONTENT_TYPE = 'application/json'

function buildFetchOptions({ method, body, customHeaders, options }) {
  const fetchOptions = {
    method: method,
    credentials: 'include',
    headers: { ...headers, ...customHeaders },
  }
  if (options) {
    Object.assign(fetchOptions, options)
  }
  if (body) {
    fetchOptions.body = JSON.stringify(body)
  }
  return fetchOptions
}

/**
 * Quem decide se a resposta é um stream é o `content-type` que o SERVIDOR
 * mandou — nunca o `Accept` que o cliente pediu.
 *
 * Até a spec-001 do `project-plataforma-por-chat`, bastava ter pedido
 * `Accept: text/event-stream` para o `Response` voltar CRU. Um envelope de erro
 * em HTTP 200 (`{status: 500, message}`, o contrato da casa) chegava então ao
 * parser SSE como se fosse stream: o `apiChat` drenava um corpo que nunca teve
 * um único `event:`, nenhum handler rodava, e o turno morria em silêncio — a
 * bolha vazia e o loading preso. Lendo o `content-type`, o envelope volta a ser
 * lido como JSON e o chamador consegue mostrar o erro (US-3).
 */
function parseResponseBody(response) {
  const cabecalhos = response && response.headers
  const contentType = cabecalhos ? cabecalhos.get('content-type') : null
  if (contentType && contentType.includes(STREAM_CONTENT_TYPE)) {
    return response
  }
  if (contentType && contentType.includes(JSON_CONTENT_TYPE)) {
    return response.json()
  }
  return response.text()
}

/**
 * Envia uma requisição SSE (Server-Sent Events) para o módulo IA CRM.
 *
 * USO RESTRITO: somente para endpoints SSE (Accept: text/event-stream) — POST /chat
 * e GET /chat/reconnect. Endpoints síncronos (status/cancel/conversations) devem
 * usar `@services/connection` (axios), que carrega interceptors de 401, CSRF e
 * error transformation padronizados.
 *
 * Retorna o objeto Response CRU somente quando o servidor respondeu
 * `content-type: text/event-stream`. Para erros de rede, HTTP não-ok ou
 * resposta JSON (inclusive o envelope de erro em HTTP 200), retorna o corpo
 * lido — shape {status, message, value}.
 */
export async function iaCrmRequest({ method, endpoint, body, customHeaders, options }) {
  const resolvedHeaders = customHeaders || {}
  const resolvedOptions = options || {}
  const resolvedBody = body || null

  let response
  try {
    response = await fetch(
      API_URL + endpoint,
      buildFetchOptions({ method: method, body: resolvedBody, customHeaders: resolvedHeaders, options: resolvedOptions })
    )
  } catch (error) {
    return desfechoDaFalha(error, 'HTTP Request error:')
  }

  if (!response.ok) {
    return {
      status: response.status,
      message: response.statusText || 'Erro inesperado',
      value: null,
    }
  }

  try {
    return await parseResponseBody(response)
  } catch (error) {
    return desfechoDaFalha(error, 'HTTP Request parse error:')
  }
}

/**
 * Só um `Response` de verdade tem `body.getReader`.
 *
 * A porta de entrada do stream: `iaCrmRequest` só devolve o `Response` cru
 * quando o SERVIDOR respondeu `content-type: text/event-stream`. Um envelope
 * JSON em HTTP 200 chega como objeto simples e NÃO é drenado como stream.
 * Compartilhado por `apiChat` e `apiAdvisory` — dois parsers de frame, uma
 * porta de entrada.
 */
export function isStreamResponse(response) {
  return Boolean(response && response.body && response.body.getReader)
}

/** A primeira linha completa do buffer SSE, ou `null` se ainda não fechou. */
export function getLineFromBuffer(buffer) {
  const idx = buffer.indexOf('\n')
  if (idx === -1) return null
  return buffer.substring(0, idx)
}
